import React, { useState } from "react";
import * as Comps from "../../../../CONFIG_PARSER/TableCellBlocks";
import { colors2, getVal, LoaderOverlay } from 'oolib';
import { blockIsPopulatedConditions } from '../../../../../../../utils/validation/blockIsPopulatedConditions';
import { StyledRowCellContentWrapper, StyledTableRowCell } from "../../../../styled";
import { useTheme } from "styled-components";
import { dynamicPropsFnLookup, generateDynamicProps } from "./utils/generateDynamicProps";
import { __CheckAccessViaConfig } from "../../../../../../../utils/accessControl";
import { useGetQueryData } from "../../../../../../../utils/react-query-hooks/general";


const defaultReadOnlyFalseComps = ['TableTextInput']

//basically all input blocks are not required to be populated
const okToNotBePopulatedBlocks = [
  'TableTextInput',
  'TableDropdownSingle',
  'TableToggleButton',
  'TableDirectImportFieldSelector',
  'TableStaticText' // renders fixed text, ignores row data — a cell value can't exist
]

const setPaddingRightBasedOnComp = (cellComp) => {
  return ![
    'TableTextInput', 'TableTags', // cuz in these 2 comp cases we want the text/tag to bleed off the right edge
    'TableDropdownSingle', // and in this has padding of its own, so, cell's padding not needed
    'TableToggleButton',
    'TableDirectImportFieldSelector'
  ].includes(cellComp) ? '1rem' : 'unset'
}

const setPaddingLeftBasedOnComp = (cellComp) => {
  return ![
    'TableDropdownSingle',
    'TableDirectImportFieldSelector',
    'TableToggleButton'
    //in these has padding of its own, so, cell's padding not needed
  ].includes(cellComp) ? '1rem' : 'unset'
}

const setRightBleedFadeBasedOnComp = (cellComp) => {
  return ['TableTags'].includes(cellComp) ? true : false // this is basically that gradient fade thingy.. just trying it out.
}

const TableCell = ({
    tableConfig,
    colConfig,
    data : tableRowData,
    rowId,
    colWidthConfig,
    handleCellChange,
    cellIdx,
    isLastRow,
    isTableEdge,
    rowIdx,
    disabled,
    disableRowStripes
}) => {

  const userData = useGetQueryData('userData')
  const { tableElem_handleCellChangeFns } = useTheme() || {}

  const readOnly = defaultReadOnlyFalseComps.includes(colConfig.comp) ? false : true;

  const [showHandleCellChangeLoader, setShowHandleCellChangeLoader] = useState(undefined)

  const genCell = (colConfig) => {

    const highlight = colConfig.highlightFn
          ? dynamicPropsFnLookup[colConfig.highlightFn]({colConfig, tableRowData})
          : false

    const genCellComp = (colConfig) => {
        let compName = colConfig.comp || "TableText";

        const renderBasedOnDisplayCondition = colConfig?.displayConditionCallback 
          ? colConfig?.displayConditionCallback({colConfig, tableRowData})
          : true;

        if(renderBasedOnDisplayCondition === false) return null;

        const dynamicProps = colConfig?.dynamicPropsFn
          ? generateDynamicProps({dynamicPropsFn: colConfig.dynamicPropsFn, colConfig, tableRowData})
          : colConfig.dynamicPropsCallback
            ? colConfig?.dynamicPropsCallback({colConfig, tableRowData})
            : {}

        let props = {
          readOnly,
          tableRowData,
          ...(colConfig.props || {}),
          value: colConfig.valuePath !== undefined //explicitly we say undefined, cuz null is a valid valuePath in getVal
            ? getVal(tableRowData, colConfig.valuePath) &&
            getVal(getVal(tableRowData, colConfig.valuePath), colConfig.propertyPath || null)
            : // we do this cuz we need to find the final property that has a 'displayable' string to show in the dom
            colConfig.value
            ? colConfig.value
            : undefined,
          ...dynamicProps
        };
  

        const allowAccessToThisCell = colConfig.accessConfig
          ? __CheckAccessViaConfig({
            user: userData?.user,
            action: colConfig.accessConfig.action,
            checkAccessTo: colConfig.accessConfig.checkAccessTo,
            resourceType: colConfig.accessConfig.resourceType || getVal(tableRowData, colConfig.accessConfig.resourceTypePath)
          })
          : true



        if(
          (!okToNotBePopulatedBlocks.includes(compName) && !blockIsPopulatedConditions({comp: compName, props}, props.value))
          || !allowAccessToThisCell
        ){
          compName = 'TableText'
          props = { value: '-' }
        }



        let Comp = Comps[compName];
        
  
        return (
          
          <Comp
            {...props}
            id={rowId+colConfig.colId}
            /**
             * handleCellChange can directly be passed as a function prop to
             * TableElem or, it can be passed as tableConfig.handleCellChangeFn into the 
             * tableConfig, and this will work only if the same named function is made available
             * in OolibThemeProvider
             */
            { ...(
              handleCellChange
              ? {onChange: (k,v) => {
                handleCellChange(tableRowData, colConfig, v, {rowIdx})
              }}
              : (tableConfig.handleCellChangeFn &&
                tableElem_handleCellChangeFns &&
                tableElem_handleCellChangeFns[tableConfig.handleCellChangeFn] )
                ? {onChange: (k,v) => {
                  tableElem_handleCellChangeFns[tableConfig.handleCellChangeFn](tableRowData, colConfig, v, setShowHandleCellChangeLoader)
                }}
                : {}
            )}
            // onChange={(k,v) => {
            //   handleCellChange(tableRowData, colConfig, v)
            // }}
          />
          
        );
      };

      const rowBgColor =
        !disableRowStripes && rowIdx % 2 !== 0 ? colors2.grey5 : colors2.white;

      return (
        <>
        {showHandleCellChangeLoader && <LoaderOverlay {...showHandleCellChangeLoader}/>}
        <StyledTableRowCell
          freezeFirstCol={tableConfig.freezeFirstCol}
          highlight={highlight}
          cellIdx={cellIdx}
          isLastRow={isLastRow}
          isTableEdge={isTableEdge}
          rowIdx={rowIdx}
          data-col-id={colConfig.colId}
          style={{
            width: colWidthConfig[colConfig.colId],
          }}
          >
            {colConfig.subColsConfig ? (
              <div style={{display: 'flex', height: '100%'}}>
                {colConfig.subColsConfig.map((dd) => (
                  <StyledRowCellContentWrapper
                    style={{
                      width: `${100 / colConfig.subColsConfig.length}%`,
                      paddingRight: setPaddingRightBasedOnComp(dd.comp),
                      paddingLeft: setPaddingLeftBasedOnComp(dd.comp)
                    }}
                    rightBleed={setRightBleedFadeBasedOnComp(dd.comp)}
                    bleedColor={rowBgColor}
                    >
                    {genCellComp(dd)}
                  </StyledRowCellContentWrapper>
                ))}
              </div>
            ) : (
              <StyledRowCellContentWrapper 
                style={{
                  paddingRight: setPaddingRightBasedOnComp(colConfig.comp),
                  paddingLeft: setPaddingLeftBasedOnComp(colConfig.comp),
                  backgroundColor: disabled ? colors2.grey5 : 'unset',
                  color: disabled ? colors2.grey40 : 'unset'
                }}
                rightBleed={disabled ? false : setRightBleedFadeBasedOnComp(colConfig.comp)}
                bleedColor={disabled ? colors2.grey5 : rowBgColor}
                >
                {genCellComp(colConfig)}
              </StyledRowCellContentWrapper>
              
            )}
          
        </StyledTableRowCell>
        </>
      );
  }

  return genCell(colConfig);
};

export default React.memo(TableCell);
